/* One shared pointer listener for every Francisco on the page (ROBOT.md §4).
   The pointer position is stored; ONE requestAnimationFrame then measures each tracked figure and
   hands it the direction to the pointer (dx, dy in [-1, 1], relative to the window size).
   Only changes above 0.02 are applied, and transforms go straight to the DOM (no React re-render). */

export type LookHandler = (dx: number, dy: number) => void

type Tracked = { el: Element; apply: LookHandler; last: [number, number] | null }

const tracked = new Set<Tracked>()
let pointer: { x: number; y: number } | null = null
let frame = 0
let listening = false

const clamp = (v: number) => Math.max(-1, Math.min(1, v))

function update() {
    frame = 0
    const w = window.innerWidth || 1
    const h = window.innerHeight || 1
    for (const t of tracked) {
        let next: [number, number] = [0, 0]
        if (pointer) {
            const box = t.el.getBoundingClientRect()
            if (!box.width && !box.height) continue
            next = [clamp((pointer.x - (box.left + box.width / 2)) / w), clamp((pointer.y - (box.top + box.height / 2)) / h)]
        }
        const last = t.last
        if (!last || Math.abs(last[0] - next[0]) > 0.02 || Math.abs(last[1] - next[1]) > 0.02 || (!pointer && (last[0] || last[1]))) {
            t.last = next
            t.apply(next[0], next[1])
        }
    }
}

function schedule() {
    if (!frame) frame = window.requestAnimationFrame(update)
}

function onMove(e: PointerEvent) {
    pointer = { x: e.clientX, y: e.clientY }
    schedule()
}

/* Pointer left the window: everyone eases back to the centre. */
function onOut(e: MouseEvent) {
    if (e.relatedTarget) return
    pointer = null
    schedule()
}

function listen(on: boolean) {
    if (on === listening) return
    listening = on
    if (on) {
        window.addEventListener('pointermove', onMove, { passive: true })
        document.addEventListener('mouseout', onOut)
    } else {
        window.removeEventListener('pointermove', onMove)
        document.removeEventListener('mouseout', onOut)
        if (frame) window.cancelAnimationFrame(frame)
        frame = 0
    }
}

/** Start following the pointer; returns the function that stops it. */
export function trackPointer(el: Element, apply: LookHandler): () => void {
    const entry: Tracked = { el, apply, last: null }
    tracked.add(entry)
    listen(true)
    if (pointer) schedule()
    return () => {
        tracked.delete(entry)
        if (!tracked.size) listen(false)
    }
}
